import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormInputText } from "../sharedComponents/form-components/FormInputText";
import { FormMultilineInputText } from "../sharedComponents/form-components/FormMultilineInputText";
import {
  getActionDetails,
  getActionGroups,
  getModelsList,
  upsertFlowAction,
} from "../../service/ApiService";
import { useLocation } from "react-router-dom";
import AlertMsg from "../sharedComponents/Alert";
import { FormInputDropdown } from "../sharedComponents/form-components/FormInputDropdown";
import { FormInputCheckbox } from "../sharedComponents/form-components/FormInputCheckbox";
import { Grid } from "@mui/material";
import ColorPicker from "../sharedComponents/form-components/ColorPicker";
import MediaInput from "../sharedComponents/form-components/mediaInput";

interface IFormInput {
  id: number;
  name: string;
  input: string;
  isFinalResult: boolean;
  isCopyAIContentDisabled: boolean;
  sortOrder: number;
  gptModelId: number;
  temperature: number;
  maxTokens: number;
  outputTypeId: number;
  topP: number;
  systemRole: string;
  icon:string;
  lineColor:string;
  actionGroupId:string
}

const outputTypeOptions: any = [
  { value: 1, label: "String" },
  { value: 2, label: "Json" },
  { value: 3, label: "Array" },
];

export const UpsertAction = ({
  isResetForm,
  isSubmitForm,
  setIsInvalidForm,
  setFormSubmitted,
  dataToSet,
  toggleLoader
}: {
  isResetForm: boolean;
  isSubmitForm: boolean;
  setIsInvalidForm: (isValid: boolean) => void;
  setFormSubmitted: (isValid: boolean) => void;
  dataToSet?: any;
  toggleLoader?:any}) => {
  const { handleSubmit, reset, control, setValue, formState } =
    useForm<IFormInput>({
      defaultValues: {
        id: 0,
        name: "",
        input: "",
        isFinalResult: false,
        isCopyAIContentDisabled: false,
        sortOrder: 0,
        gptModelId: 0,
        temperature: 0,
        maxTokens: 0,
        outputTypeId: 0,
        topP: 0,
        systemRole: "",
        icon:'',
        lineColor:'',
        actionGroupId:"",
      },
      mode: "onChange",
    });

  const { isValid } = formState;
  const location = useLocation();
  const [modelsList, setModelsList] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertData, setAlertData] = useState<any>();
  const [selectedModel, setSelectedModel] = useState<any>();
  const [actionDetails, setActionDetails] = useState<any>();
  const [actionGroupsList, setActionGroupsList] = useState<any>([]);

  useEffect(() => {
    getactionGroupsList()
    const clientCode: any = new URLSearchParams(location.search).get(
      "clientCode"
    );
    getModelsList(clientCode)
      .then((response: any) => {
        if (
          response &&
          response.Model &&
          response.Model.values &&
          response.Model.values.length > 0
        ) {
          setModelsList(response.Model.values);
        }
      })
      .catch((error: any) => {
        console.log("error", error);
      });
  }, []);

  useEffect(() => {
    if (dataToSet?.id) {     
      getActionDetails(dataToSet.id)
        .then((res: any) => {
          if (res && Object.keys(res).length > 0) {         
            setActionDetails(res);
          }
        })
        .catch((error: any) => console.log("error", error));
    }
  }, [dataToSet]);

    const getactionGroupsList = () => { 
      const flowId: any = new URLSearchParams(location.search).get("flowId");
      getActionGroups(flowId).then((response: any) => {
          setActionGroupsList([]);
          if (response && response.length > 0) {
            const formattedOptions = response.map((group:any) => ({
              value: group.id, // Convert `id` to `value`
              label: group.title, // Convert `title` to `label`
            }));
            setActionGroupsList(formattedOptions);
          }
        })
        .catch((error: any) => console.log("error", error));
    };

  useEffect(() => {
    if (actionDetails && Object.keys(actionDetails).length > 0) {
      setValue("id", actionDetails.id ?? 0);
      setValue("name", actionDetails.name ?? "");
      setValue("input", actionDetails.input ?? "");
      setValue("isFinalResult", actionDetails.isFinalResult ?? false);
      setValue("isCopyAIContentDisabled", actionDetails.isCopyAIContentDisabled ?? false);
      setValue("sortOrder", actionDetails.sortOrder ?? 0);
      setValue("icon", actionDetails.icon ?? "");
      setValue("lineColor", actionDetails.settings?.lineColor ?? "");
      setValue("gptModelId", actionDetails.settings?.gptModelId ?? 0);
      setValue("temperature", actionDetails.settings?.temperature ?? 0);
      setValue("maxTokens", actionDetails.settings?.maxTokens ?? 0);
      setValue("outputTypeId", actionDetails.settings?.outputTypeId ?? 0);
      setValue("topP", actionDetails.settings?.topP ?? 0);
      setValue("systemRole", actionDetails.settings?.systemRole ?? "");
      setValue("actionGroupId", actionDetails?.actionGroupId ?? "");
      if (actionDetails.settings?.gptModelId > 0) {
        const model = modelsList.find(
          (m: any) => m.id === actionDetails.settings?.gptModelId
        );
        setSelectedModel(model || null);
      }
    }
  }, [actionDetails, setValue]);

  const openTheSnackbar = () => {
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  useEffect(() => {
    if (isResetForm) {
      reset();
    }
  }, [isResetForm]);

  useEffect(() => {
    setIsInvalidForm(isValid);
  }, [isValid, setIsInvalidForm]);

  useEffect(() => {
    const flowId: any = new URLSearchParams(location.search).get("flowId");
    if (isSubmitForm) {
      handleSubmit((data: any) => {
        const payload: any = {
          ...actionDetails,
          id: data.id,
          name: data.name,
          input: data.input,
          isFinalResult: data.isFinalResult,
          isCopyAIContentDisabled: data.isCopyAIContentDisabled,
          sortOrder: data.sortOrder,
          icon: data.icon,   
          actionGroupId:data.actionGroupId || null,       
          settings: {
            temperature: data.temperature,
            maxTokens: data.maxTokens,
            outputTypeId: data.outputTypeId,
            gptModelId: data.gptModelId,
            topP: data.topP,
            systemRole: data.systemRole,
            lineColor: data.lineColor,
          },
        };
        console.log(payload);
        upsertFlowAction(payload, flowId).then((response: any) => {
          openTheSnackbar();
          toggleLoader(true);
          if (response.success) {
            setAlertData({ "type": true, "msg": `The action has been ${actionDetails?.id ? 'updated': 'created'} successfully` });
          } else {
            setAlertData({ "type": false, "msg": response.errorMessage ?? `The action ${actionDetails?.id ? 'updation': 'creation'} was unsuccessful` });
            toggleLoader(false);
          }
          setTimeout(() => {
            if (response.success) {
              toggleLoader(false);
              reset();
              setFormSubmitted(true);
            }
          }, 2000);
        });
      })();
    }
  }, [isSubmitForm]);

  return (
    <div>
      <form>
        <FormInputText name="name" control={control} label="Name" required />
        <FormMultilineInputText
          name="input"
          control={control}
          label="Input"
          required
          minRows={6}
        />        
        <FormInputDropdown
          name="gptModelId"
          control={control}
          label="Model"
          options={modelsList.map((model: any) => ({
            value: model.id,
            label: model.label,
          }))}
          required
          onChange={(e: any) => {
            const model = modelsList.find((m: any) => m.id === e);
            setSelectedModel(model || null);
          }}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <div className="mb-3 relative">
              <FormInputText
                name="temperature"
                control={control}
                label="Temperature"
                required
                type="number"
                max={2.0}
                maxLength={4}
              />
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  right: 0,
                  top: "80%",
                  fontSize: 10,
                  textAlign: "end",
                }}
              >
                The temperature value should be between 0.0 and 2.0
              </div>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="mb-3 relative">
              <FormInputText
                name="maxTokens"
                control={control}
                label="Max Token"
                required
                type="number"
                min={selectedModel?.min}
                max={selectedModel?.max}
                maxLength={selectedModel?.max?.toString().length}
              />
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  right: 0,
                  top: "80%",
                  fontSize: 10,
                  textAlign: "end",
                }}
              >
                {selectedModel
                  ? `The Max Token value should be between ${selectedModel?.min} and ${selectedModel?.max}`
                  : ""}
              </div>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormInputText
              name="sortOrder"
              control={control}
              label="Sort Order"
              required
              type="number"
            />
          </Grid>
          <Grid item xs={6}>
            <FormInputDropdown
              name="outputTypeId"
              control={control}
              label="Output Type"
              options={outputTypeOptions}
              required
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
        <Grid item xs={6}>
        <FormInputText
          name="topP"
          control={control}
          label="Top P"
          required
          type="number"
        />
        </Grid>
        <Grid item xs={6}>
            <FormInputDropdown
              name="actionGroupId"
              control={control}
              label="Action group"
              options={actionGroupsList}              
            />
          </Grid>
        </Grid>
        <FormMultilineInputText
          name="systemRole"
          control={control}
          label="System Role"
          minRows={6}
        />
         <Grid container spacing={2}>
          <Grid item xs={6}>
          <ColorPicker
            name="lineColor"
            control={control} 
            label="Color code" 
            isInlineCss={true}    
          />
          </Grid>
          <Grid item xs={6}>
           <MediaInput
           name="icon"
           control={control}
           label="Color code"
            />
          </Grid>
        </Grid>
        <FormInputCheckbox
          name="isFinalResult"
          control={control}
          setValue={setValue}
          label="Is final result"
          required
        />  
         <FormInputCheckbox
          name="isCopyAIContentDisabled"
          control={control}
          setValue={setValue}
          label="Disable copy AI generated content"
          required
        />     
      </form>

      <AlertMsg
        openSnackbar={openSnackbar}
        alertdata={alertData}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};
